import { formatDeliveryDate } from '@/pages/OrdersHistory/components/OrderHistoryContent/columns/DeliveryTypeColumn/utils.ts'
import { ORDERS_PAGE as dict } from '@/resources/dict'
import { DeliveryType, OrderStatus } from '@/resources/enums'
import { deliveryTypeLabel } from '@/resources/enums/deliveryTypes.ts'
import { TURBO_COLORS, TurboColor } from '@/resources/enums/orders'
import type { ActiveOrder, DeliveryDate } from '@/resources/types'
import { addTimeZone, isToday, isTomorrow } from '@/resources/utils/global'
import { addDays } from 'date-fns'

type DateValue = string | Date

export const isOrderLate = (date: string, deliveryTime: number) => {
  const scheduledDate = addDays(addTimeZone(date), 1)
  scheduledDate.setHours(deliveryTime, 0, 0, 0)

  return scheduledDate < new Date()
}

export const getDeliveryDay = (
  deliveryDay: DateValue | null,
  deliveryDate?: DateValue
) => {
  if (deliveryDate && isToday(deliveryDate))
    return dict.kanban.scheduled.delivery.today
  if (deliveryDate && isTomorrow(deliveryDate))
    return dict.kanban.scheduled.delivery.tomorrow
  return String(deliveryDay)
}

export const isOrderWithDeliveryType = (
  orderType: DeliveryType,
  deliveryType: DeliveryType
) => orderType === deliveryType

export const getInSeparationOrdersWithDeliveryType = (
  orders: ActiveOrder[] = [],
  deliveryType = DeliveryType.RECEIVE
) => {
  return orders.filter(
    ({ status, delivery }) =>
      ((status as OrderStatus) === OrderStatus.POC_ACCEPTED ||
        (status as OrderStatus) === OrderStatus.DM_ACCEPTED) &&
      isOrderWithDeliveryType(delivery.type, deliveryType)
  )
}

export const formatDelivery = (
  startDate?: DateValue | null,
  endDate?: DateValue | null,
  isAlert = false
) => {
  if (!startDate || !endDate) return ''

  const start = new Date(startDate)
  const end = new Date(endDate)

  const endTime = end.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })

  if (isAlert) {
    const startFormatted = start.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
    })
    const startTime = start.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    })

    return `${dict.kanban.choppSchedule.to} ${startFormatted} ${dict.kanban.choppSchedule.tag[1]} ${startTime} ${dict.kanban.choppSchedule.tag[2]} ${endTime}`
  }

  const startDay = start.toLocaleDateString('pt-BR', { weekday: 'long' })
  const startFormatted = start.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
  const startTime = start.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })

  return `${dict.kanban.choppSchedule.tag[0]} ${startDay} ${startFormatted} ${dict.kanban.choppSchedule.tag[1]} ${startTime} ${dict.kanban.choppSchedule.tag[2]} ${endTime}`
}

export const getOrderColorByDeliveryType = (
  type: DeliveryType,
  turboColor?: TurboColor
) => {
  switch (type) {
    case DeliveryType.TURBO:
      return turboColor ? TURBO_COLORS[turboColor] : 'var(--flavor-70)'
    case DeliveryType.SCHEDULED:
      return 'var(--primitive-solid-purple-70)'
    case DeliveryType.PICKUP:
      return 'var(--primitive-solid-blue-70)'
    case DeliveryType.RECEIVE:
    default:
      return 'var(--primitive-transparent-black-16)'
  }
}

export const getOrderDeliveryTypeLabel = (
  type: DeliveryType,
  deliveryDate?: DeliveryDate | null
) => {
  let typeLabel = deliveryTypeLabel[type]
  if (deliveryDate?.startDate && deliveryDate.endDate) {
    typeLabel += `: ${formatDeliveryDate(deliveryDate)}`
  }
  return typeLabel
}
